import type React from 'react';
import { useEffect, useState } from 'react';

/**
 * The visible area above the on-screen keyboard (null off mobile / without visualViewport).
 * The native WebView doesn't shrink the layout viewport when the keyboard opens, so a
 * full-screen dialog sized to 100% keeps its lower fields and actions under the keyboard.
 */
export function useKeyboardSafeViewport(enabled: boolean) {
  const [box, setBox] = useState<{ height: number; top: number } | null>(null);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!enabled || !vv) { setBox(null); return; }
    const sync = () => setBox({ height: Math.round(vv.height), top: Math.round(vv.offsetTop) });
    sync();
    vv.addEventListener('resize', sync);
    vv.addEventListener('scroll', sync);
    return () => {
      vv.removeEventListener('resize', sync);
      vv.removeEventListener('scroll', sync);
    };
  }, [enabled]);
  return box;
}

/**
 * Lift a focused field to the top of the dialog once the keyboard has finished sliding up,
 * so the field and the next step below it sit in the part of the screen the keyboard leaves.
 */
export const revealOnFocus = (e: React.FocusEvent<HTMLElement>) => {
  const el = e.currentTarget;
  window.setTimeout(() => el.scrollIntoView({ block: 'start', behavior: 'smooth' }), 300);
};
